import React, { useState, useEffect, useRef } from 'react'
import styles from './SkewCarousel.module.scss'
import reviewData from './reviewValue.json'
import { useIsMobile } from '../../../hooks/responsive'

const CARD_WIDTHS = [320, 313, 288, 313, 320] // left1, left2, center, right2, right1
const GAP = 16
const MARGIN = 32

const SkewCarousel = () => {
	const [isMobileCarousel, setIsMobileCarousel] = useState(false)
	const [currentIdx, setCurrentIdx] = useState(0)
	const scrollContainerRef = useRef(null)
	const cardRef = useRef(null)

	const isMobile = useIsMobile()

	// 5개 카드 전체 width 계산
	const getTotalWidth = () => {
		const cardWidths = CARD_WIDTHS.reduce((a, b) => a + b, 0)
		const totalGap = GAP * 4
		const totalMargin = MARGIN * 2
		return cardWidths + totalGap + totalMargin
	}

	useEffect(() => {
		const handleResize = () => {
			setIsMobileCarousel(window.innerWidth < getTotalWidth())
		}
		window.addEventListener('resize', handleResize)
		handleResize()
		return () => window.removeEventListener('resize', handleResize)
	}, [])

	// 카드 한 장의 width(px) 측정 (ref 사용)
	const getCardWidth = () => {
		if (cardRef.current) {
			return cardRef.current.offsetWidth
		}
		return window.innerWidth < 480 ? 288 : 450 // fallback
	}

	// 스크롤 이벤트 핸들러
	const handleScroll = () => {
		if (!scrollContainerRef.current) return
		const scrollLeft = scrollContainerRef.current.scrollLeft
		const cardWidth = getCardWidth()
		const gap = 16
		const paddingLeft = (window.innerWidth - cardWidth) / 2
		// 각 카드의 중앙 위치: paddingLeft + idx * (cardWidth + gap) + cardWidth / 2
		// 현재 뷰포트 중앙 위치: scrollLeft + window.innerWidth / 2
		// 가장 가까운 카드 찾기
		const viewportCenter = scrollLeft + window.innerWidth / 2
		const newIdx = Math.round(
			(viewportCenter - paddingLeft - cardWidth / 2) / (cardWidth + gap),
		)
		const clampedIdx = Math.max(0, Math.min(newIdx, reviewData.length - 1))
		if (clampedIdx !== currentIdx) {
			setCurrentIdx(clampedIdx)
		}
	}

	// 5개만 보여주므로, centerIdx 기준 좌2, 좌1, center, 우1, 우2
	const getDisplayItems = (centerIdx) => {
		const items = []
		for (let i = -2; i <= 2; i++) {
			const idx = centerIdx + i
			if (idx >= 0 && idx < reviewData.length) {
				items.push({ ...reviewData[idx], pos: i })
			}
		}
		return items
	}

	// 스크롤 기반 모바일 카드 렌더링
	const renderMobileCards = () => {
		const cardWidth = getCardWidth()
		const gap = 16

		return (
			<div
				ref={scrollContainerRef}
				className={styles.mobileCardTrack}
				onScroll={handleScroll}
				style={{
					display: 'flex',
					overflowX: 'auto',
					overflowY: 'hidden',
					scrollSnapType: 'x mandatory',
					scrollBehavior: 'smooth',
					WebkitOverflowScrolling: 'touch',
					scrollbarWidth: 'none',
					msOverflowStyle: 'none',
					width: '100%',
					height: '100%',
					gap: `${gap}px`,
					paddingLeft: 'calc(50% - ' + cardWidth / 2 + 'px)',
					paddingRight: 'calc(50% - ' + cardWidth / 2 + 'px)',
				}}>
				{reviewData.map((item, idx) => (
					<div
						key={idx}
						ref={idx === 0 ? cardRef : null}
						className={styles.mobileCard}
						style={{
							flexShrink: 0,
							width: `${cardWidth}px`,
							scrollSnapAlign: 'center',
							scrollSnapStop: 'always',
						}}>
						<span className={styles.mobileReviewHeader}>{item.header}</span>
						<div className={styles.mobileReviewBody}>{item.body}</div>
						<div className={styles.mobileReviewBottom}>
							<span>{item.name}</span>
							<span>{item.where}</span>
						</div>
					</div>
				))}
			</div>
		)
	}

	return (
		<div className={styles.skewCarouselWrapper}>
			{isMobile ? (
				<>
					<div className={styles.skewCarouselContainer} style={{ overflow: 'hidden' }}>
						<div
							className={styles.mobileCardWrapper}
							style={{ position: 'relative', width: '100%', height: '100%' }}>
							{renderMobileCards()}
						</div>
					</div>
					{/* dot pagination */}
					<div className={styles.carouselDots}>
						{reviewData.map((_, idx) => (
							<span
								key={idx}
								className={
									styles.carouselDot +
									(idx === currentIdx ? ' ' + styles.activeDot : '')
								}
							/>
						))}
					</div>
				</>
			) : (
				// 5개 카드 모두 보여줌 (정적)
				<div className={styles.skewCarouselContainer}>
					{getDisplayItems(2).map((item) => {
						const { id, header, body, name, where, pos } = item
						let cardClass = ''
						if (pos === -2) cardClass = styles.left1
						else if (pos === -1) cardClass = styles.left2
						else if (pos === 0) cardClass = styles.center
						else if (pos === 1) cardClass = styles.right2
						else if (pos === 2) cardClass = styles.right1
						return (
							<div key={id} className={cardClass}>
								<span className={styles.reviewHeader}>{header}</span>
								<div className={styles.reviewBody}>{body}</div>
								<div className={styles.reviewBottom}>
									<span>{name}</span>
									<span className={styles.reviewWhere}>{where}</span>
								</div>
							</div>
						)
					})}
				</div>
			)}
		</div>
	)
}

export default SkewCarousel
